import React, { useCallback } from 'react';
import { MultiPanePanel, DiscountPanel } from '@vp/component-library';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useSiteData from '../../hooks/useSiteData';
import { DiscountModalProps } from '../../types/discount.types';
import { pricingSelectors } from '../../state/pricing';
import { cartItemSelectors } from '../../state/cartItems';
import { RootState } from '../../types';

export const DiscountModal = ({
  open,
  setIsOpen,
  discounts,
  selectedDiscountId,
}: DiscountModalProps) => {
  const { locale } = useSiteData();

  const itemPricingData = useSelector((state: RootState) =>
    pricingSelectors.selectItemPricing(state.pricing)
  );

  const cartItemsMap = useSelector((state: RootState) =>
    cartItemSelectors.selectCartItemsMap(state.cartItems)
  );

  const itemSelector = useCallback(
    (promotionId: string) => {
      // get cart item ids from item pricing
      const cartItems = Object.keys(itemPricingData).reduce(
        (acc: string[], value) => {
          // flatten the discountSources for the cart item
          const itemPromotions = itemPricingData[value].discountSources.reduce(
            (accumulator: string[], currentValue: any) =>
              accumulator.concat(currentValue?.promotionId),
            []
          );
          // does the cart item have the promotionId as a discountSource?
          if (itemPromotions.includes(promotionId)) {
            return [...acc, value];
          }
          return acc;
        },
        []
      );

      // return the Cart items that match the promotion
      return Object.values(cartItemsMap)
        .filter((item: any) => cartItems.includes(item.id))
        .map((item: any) => ({
          id: item.id,
          productName: item.merchandisingData.productName || '',
          previewUrl: item.designData.livePreviewUrl || '',
          quantity: item.fulfillmentData.fulfilledQuantity,
        }));
    },
    [cartItemsMap, itemPricingData]
  );

  return (
    <MultiPanePanel
      isOpen={open}
      setIsOpen={setIsOpen}
      locale={locale}
      onlyRenderWhenOpen
    >
      <DiscountPanel
        locale={locale}
        discountSources={discounts}
        selectedDiscountId={selectedDiscountId}
        itemSelector={itemSelector}
      />
    </MultiPanePanel>
  );
};

DiscountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  discounts: PropTypes.shape({
    itemDiscounts: PropTypes.array,
    shippingDiscounts: PropTypes.array,
  }).isRequired,
  selectedDiscountId: PropTypes.string.isRequired,
};
